import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ClientTrialBlock from '@/components/advisor/ClientTrialBlock';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { Building2, Loader2, Save, ImageIcon } from 'lucide-react';
import MarkdownEditor from '@/components/advisor/common/MarkdownEditor';
import { useState, useEffect, useRef } from 'react';
interface AdvisorClient {
  id: string;
  company_name: string;
  description: string;
  logo: string | null;
  logo_url: string | null;
  status: string;
  status_display?: string;
}



export default function ClientOverviewPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: client, isLoading } = useQuery<AdvisorClient>({
    queryKey: ['advisor-client', id],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get(`/advisor/clients/${id}/`);
      return res.data;
    },
    enabled: !!id,
  });

  const [fields, setFields] = useState({ company_name: '', description: '' });
  const [dirty, setDirty] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (client) {
      setFields({
        company_name: client.company_name || '',
        description: client.description || '',
      });
      if (client.logo_url && !logoUrl) setLogoUrl(client.logo_url);
      setDirty(false);
    }
  }, [client]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.patch(`/advisor/clients/${id}/`, fields);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-client', id] });
      queryClient.invalidateQueries({ queryKey: ['advisor-client-nav', id] });
      setDirty(false);
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      const api = await authenticatedBitswanBackendInstance();
      const formData = new FormData();
      formData.append('image', file);
      const uploadRes = await api.post('/advisor/content-images/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await api.patch(`/advisor/clients/${id}/`, { logo: uploadRes.data.id });
      return uploadRes.data;
    },
    onSuccess: (data) => {
      setLogoUrl(data.url);
      queryClient.invalidateQueries({ queryKey: ['advisor-client', id] });
    },
  });

  const set = (key: string, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  }

  if (!client) {
    return <div className="p-8 text-muted-foreground">Client not found.</div>;
  }

  return (
    <div className="space-y-6">
      <ClientTrialBlock clientId={id!} />

      {/* Header with logo + name inline */}
      <div className="flex items-center gap-4">
        <div
          className="w-14 h-14 rounded-xl border-2 border-dashed border-muted-foreground/20 hover:border-amber-500/40 flex items-center justify-center cursor-pointer transition-colors overflow-hidden bg-muted/20 shrink-0"
          onClick={() => fileInputRef.current?.click()}
          data-testid="logo-upload-area"
        >
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
          ) : uploadLogoMutation.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground/30" />
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogoMutation.mutate(f); }} />
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold tracking-tight truncate">{client.company_name}</h2>
          <p className="text-muted-foreground text-sm">{client.status_display || client.status}</p>
        </div>
      </div>

      {/* Profile Edit — collapsible */}
      <Card data-testid="client-overview-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Building2 className="h-4 w-4 text-blue-500" /> Company Profile
          </CardTitle>
          <p className="text-sm text-muted-foreground" data-testid="client-profile-privacy">
            Your notes on this client. Only you and your practice see this page; the client never does.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Company Name</label>
              <Input value={fields.company_name} onChange={(e) => set('company_name', e.target.value)} data-testid="overview-company-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Description</label>
              <div className="mt-1" data-testid="overview-description">
                <MarkdownEditor
                  value={fields.description}
                  onChange={(val) => set('description', val)}
                  height="calc(100vh - 460px)"
                />
              </div>
            </div>
            {dirty && (
              <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending} data-testid="save-overview-btn">
                {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            )}
            {updateMutation.isSuccess && !dirty && <span className="text-sm text-green-600">Saved</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
